import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  destroySlickCarousel,
  initLinkedSlickWhenReady,
} from '../../Utils/carouselHelpers';
import { mapTeamMember } from '../../Utils/teamHelpers';
import { getTeamMembers } from '../../Utils/api';
import { useLanguage } from '../../i18n/LanguageContext';
import { HOME_SECTIONS } from '../../i18n/sectionCopy';
import { useLocalizedStatic } from '../../i18n/useLocalizedStatic';
import { useMediaQuery } from '../../Utils/useMediaQuery';
import HomeTeamMobileCarousel from './HomeTeamMobileCarousel';

const img = (path) => `${process.env.PUBLIC_URL}/assets/images/${path}`;

const MOBILE_TEAM_QUERY = '(max-width: 991px)';

const buildDesktopForOptions = (count) => ({
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  fade: true,
  autoplay: count > 1,
  autoplaySpeed: 5000,
  pauseOnHover: true,
  pauseOnFocus: true,
  infinite: count > 1,
  swipe: true,
  touchMove: true,
  draggable: true,
});

const buildDesktopNavOptions = () => ({
  slidesToShow: 2,
  slidesToScroll: 1,
  vertical: true,
  dots: false,
  focusOnSelect: true,
  verticalSwiping: true,
  swipe: true,
  touchMove: true,
  infinite: false,
});

const HomeTeamSection = () => {
  const teamForRef = useRef(null);
  const teamNavRef = useRef(null);
  const { locale, revision } = useLanguage();
  const copy = useLocalizedStatic(HOME_SECTIONS);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromApi, setFromApi] = useState(false);
  const isMobile = useMediaQuery(MOBILE_TEAM_QUERY);

  useEffect(() => {
    let active = true;

    getTeamMembers()
      .then((data) => {
        if (!active) return;
        const items = (data || [])
          .map(mapTeamMember)
          .filter((item) => item.name && item.role && item.photo);
        setMembers(items);
        setFromApi(true);
      })
      .catch((err) => {
        if (!active) return;
        console.warn('Failed to load team members:', err?.message || err);
        setMembers([]);
        setFromApi(false);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [locale, revision]);

  useLayoutEffect(() => {
    const forEl = teamForRef.current;
    const navEl = teamNavRef.current;
    return () => {
      destroySlickCarousel(forEl);
      destroySlickCarousel(navEl);
    };
  }, []);

  useLayoutEffect(() => {
    if (!members.length || isMobile) {
      destroySlickCarousel(teamForRef.current);
      destroySlickCarousel(teamNavRef.current);
      return undefined;
    }

    const forEl = teamForRef.current;
    const navEl = teamNavRef.current;
    const count = members.length;

    destroySlickCarousel(forEl);
    destroySlickCarousel(navEl);

    return initLinkedSlickWhenReady(
      forEl,
      navEl,
      buildDesktopForOptions(count),
      buildDesktopNavOptions(),
    );
  }, [members, isMobile]);

  return (
    <div className="rs-team team-style1 home-team-section">
      <div className="sec-title mb-45">
        <span className="sub-text">
          <img src={img('shape-1.png')} alt="Images" />
          {copy.teamSub}
        </span>
        <h2 className="title">{copy.teamTitle}</h2>
      </div>
      <div className="vertical-widget-content">
        {loading && (
          <p className="home-team-loading">{copy.teamLoading}</p>
        )}
        {!loading && fromApi && members.length === 0 && (
          <p className="home-team-empty">{copy.teamEmpty}</p>
        )}
        {!loading && !fromApi && (
          <p className="home-team-error text-danger">{copy.teamLoadError}</p>
        )}
        {!loading && members.length > 0 && isMobile && (
          <HomeTeamMobileCarousel members={members} swipeHint={copy.teamSwipeHint} />
        )}
        {!loading && members.length > 0 && !isMobile && (
          <div className="slick-part single-product-slider">
            <div className="slider slider-for team-slider-for" ref={teamForRef}>
              {members.map((member) => (
                <div key={member.id} className="team-inner-wrap">
                  <div className="image-wrap">
                    <img src={member.photo} alt={member.name} loading="lazy" />
                  </div>
                  <div className="team-content">
                    <h3 className="title">{member.name}</h3>
                    <span className="team-title">{member.role}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="slider slider-nav team-slider-nav" ref={teamNavRef}>
              {members.map((member) => (
                <div key={`nav-${member.id}`} className="images-list">
                  <img src={member.photo} alt={member.name} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeTeamSection;
